import { useEffect, useState } from 'react';

/**
 * OdooRedirect – a trampoline page that opens synchronously (same-origin,
 * no popup-blocker issues) and then waits for the opener to post the real
 * Odoo auto-login URL via a BroadcastChannel.
 *
 * Flow:
 *   Dashboard opens /odoo-redirect?channel=odoo-editor-<timestamp>
 *   → This component listens on that BroadcastChannel
 *   → Dashboard fetches the auto-login URL from the backend
 *   → Dashboard posts { url } on the channel
 *   → This component receives it and does window.location.replace(url)
 *   → Odoo auto-login authenticates the session and redirects to the editor
 */
export default function OdooRedirect() {
  const [status, setStatus] = useState('Opening editor\u2026');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const channelId = params.get('channel');

    if (!channelId) {
      setStatus('Missing channel parameter.');
      return;
    }

    const storageKey = `odoo_editor_url_${channelId}`;
    let done = false;

    const go = (url) => {
      if (done || !url) return;
      done = true;
      try { localStorage.removeItem(storageKey); } catch (_) {}
      window.location.replace(url);
    };

    // BroadcastChannel does NOT buffer messages: if the opener posts the URL
    // before this tab finishes mounting/subscribing, the message is lost and
    // the editor never opens (perceived as a timeout). To be race-proof we
    // ALSO read/poll localStorage, which the opener writes synchronously — so
    // the URL is always picked up regardless of ordering.
    const bc = new BroadcastChannel(channelId);
    bc.onmessage = (event) => go((event.data || {}).url);
    // Announce we're ready so an opener that already has the URL can re-post.
    try { bc.postMessage({ ready: true }); } catch (_) {}

    // Immediate check + poll localStorage as the reliable fallback.
    try { go(localStorage.getItem(storageKey)); } catch (_) {}
    const poll = setInterval(() => {
      try {
        const u = localStorage.getItem(storageKey);
        if (u) { clearInterval(poll); go(u); }
      } catch (_) {}
    }, 250);

    const timeout = setTimeout(() => {
      if (!done) {
        clearInterval(poll);
        bc.close();
        setStatus('Timed out waiting for editor URL. Please try again.');
      }
    }, 30000);

    return () => {
      clearTimeout(timeout);
      clearInterval(poll);
      bc.close();
    };
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: '#0f0f0f',
      color: '#fff',
      fontFamily: 'sans-serif',
      gap: '16px',
    }}>
      <div style={{
        width: 40,
        height: 40,
        border: '3px solid #7c3aed',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <span style={{ fontSize: '1rem', opacity: 0.8 }}>{status}</span>
    </div>
  );
}
